import React, { useMemo, useRef, useState } from "react";
import { SquareFunction } from "lucide-react";
import { ZP } from "../compile";
import { FUNCTION_GROUPS, ALL_FUNCTIONS } from "../functions-help";
import { getDocType, STORE_FIELD_LIST, PAYMENT_FIELDS, TAX_FIELDS, CREDIT_NOTE_FIELDS } from "../doc-types";

const ROOTS = ["order", "store", "item", "col", "var", "items", "payments", "tax", "batches", "creditNotes", "extra"];

function allTables(template) {
  const out = [];
  (template.bands || []).forEach((b) => (b.elements || []).forEach((e) => e.type === "table" && out.push(e)));
  return out;
}

function rowFieldsFor(dt, source) {
  if (source === "payments") return PAYMENT_FIELDS;
  if (source === "tax") return TAX_FIELDS;
  if (source === "creditNotes") return CREDIT_NOTE_FIELDS;
  if (source === "batches") return [...dt.item, ...(dt.batch || [])];
  return dt.item;
}

// Suggestions for the word being typed (`prefix` may contain dots).
function suggestions(prefix, ctx) {
  const { template, scope } = ctx;
  const dt = getDocType(template.docType);
  const tables = allTables(template);
  const table = scope?.tableId ? tables.find((t) => t.id === scope.tableId) : tables.find((t) => (t.source || "items") === "items") || tables[0];
  const dot = prefix.lastIndexOf(".");
  const head = dot >= 0 ? prefix.slice(0, dot) : "";
  const tail = (dot >= 0 ? prefix.slice(dot + 1) : prefix).toLowerCase();
  let list = [];
  if (head) {
    const map = {
      order: dt.order,
      store: STORE_FIELD_LIST,
      item: rowFieldsFor(dt, table?.source),
      row: rowFieldsFor(dt, table?.source),
      items: dt.item,
      batches: [...dt.item, ...(dt.batch || [])],
      payments: PAYMENT_FIELDS,
      tax: TAX_FIELDS,
      creditNotes: CREDIT_NOTE_FIELDS,
      col: (table?.columns || []).map((c) => c.key).filter(Boolean),
      var: (template.variables || []).map((v) => v.name).filter(Boolean),
      extra: Object.keys(dt.extra || {}),
    };
    if (head.startsWith("extra.") && dt.extra) list = dt.extra[head.slice(6)] || [];
    else list = map[head] || [];
    list = list.map((f) => ({ label: f, insert: `${head}.${f}`, kind: head }));
  } else {
    const inRow = scope?.kind === "row";
    list = [
      ...ROOTS.map((r) => ({ label: r, insert: r, kind: "root" })),
      ...ALL_FUNCTIONS.map((f) => ({ label: f + "()", insert: f + "(", kind: "fn" })),
      ...(inRow ? rowFieldsFor(dt, table?.source).map((f) => ({ label: f, insert: f, kind: "row" })) : []),
      ...(template.variables || []).filter((v) => v.name).map((v) => ({ label: v.name, insert: v.name, kind: "var" })),
      ...dt.order.map((f) => ({ label: f, insert: f, kind: "order" })),
      ...STORE_FIELD_LIST.map((f) => ({ label: f, insert: f, kind: "store" })),
    ];
  }
  const seen = new Set();
  return list
    .filter((s) => s.label.toLowerCase().includes(tail) && !seen.has(s.insert) && seen.add(s.insert))
    .sort((a, b) => (a.label.toLowerCase().startsWith(tail) ? 0 : 1) - (b.label.toLowerCase().startsWith(tail) ? 0 : 1))
    .slice(0, 40);
}

// Evaluates a formula (or {{ }} text) against sample data, in the given scope.
export function evaluatePreview(template, sample, src, scope, mode, format) {
  try {
    const ctx = ZP.createContext(template, sample);
    let sc = ctx.rootScope();
    if (scope?.tableId) {
      const info = ctx.tables[scope.tableId];
      if (info && scope.kind === "row") {
        const rows = ctx.tableRows(info);
        if (rows.length) sc = ctx.rowScope(info, Math.min(scope.rowIndex || 0, rows.length - 1));
        else return { value: "(no rows in sample)", note: true };
      } else if (info) sc = { ctx, row: null, rowIndex: -1, table: info, bind: null };
    }
    if (mode === "template") {
      const errors = [];
      const v = ctx.interpolate(src, sc, errors);
      return errors.length ? { error: errors[0] } : { value: v };
    }
    const r = ctx.evalSafe(src, sc);
    if (r.error) return { error: r.error };
    const shown = ZP.toStr(format ? ZP.applyFormat(r.value, format) : r.value);
    const warn = Object.keys(ctx.warnings).find((w) => src && w.includes("'"));
    return { value: shown, warn: warn && src.includes(warn.split("'")[1]?.split(".").pop()) ? warn : null };
  } catch (e) {
    return { error: e.message };
  }
}

export function FunctionHelp({ onPick }) {
  return (
    <div className="max-h-72 overflow-auto text-[11px]">
      {FUNCTION_GROUPS.map((g) => (
        <div key={g.title} className="mb-2">
          <div className="font-semibold text-000000 px-2 py-1 bg-nav-head-bg">{g.title}</div>
          {g.items.map((f) => (
            <button key={f.name} type="button" className="w-full text-left px-2 py-1 hover:bg-f3f8ff" onClick={() => onPick(f.insert)}>
              <span className="font-mono font-semibold text-primary">{f.name}</span> <span className="text-344054">{f.desc}</span>
              {f.ex && <div className="font-mono text-[10px] text-667085">{f.ex}</div>}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/*
 * mode "formula": the whole value is an expression (fields, columns, totals).
 * mode "template": static text with {{ expression | format }} placeholders.
 */
export default function FormulaInput({ value, onChange, template, sample, scope, mode = "formula", format, rows = 2, placeholder, showResult = true }) {
  const ref = useRef(null);
  const [sugg, setSugg] = useState(null); // { items, index, start, end }
  const [help, setHelp] = useState(false);

  const result = useMemo(() => {
    if (!showResult || !value || !String(value).trim()) return null;
    return evaluatePreview(template, sample, value, scope, mode, format);
  }, [value, template, sample, scope, mode, format, showResult]);

  const refresh = (text, caret) => {
    const before = text.slice(0, caret);
    if (mode === "template") {
      const open = before.lastIndexOf("{{");
      const close = before.lastIndexOf("}}");
      if (open < 0 || close > open) return setSugg(null);
    }
    const m = /[A-Za-z_][A-Za-z0-9_.]*$/.exec(before);
    if (!m || /["']/.test(before.slice(-1))) return setSugg(null);
    const quotes = (before.match(/"/g) || []).length;
    if (quotes % 2 === 1) return setSugg(null);
    const items = suggestions(m[0], { template, scope });
    if (!items.length || (items.length === 1 && items[0].insert === m[0])) return setSugg(null);
    setSugg({ items, index: 0, start: caret - m[0].length, end: caret });
  };

  const insertAt = (text, start, end, ins) => {
    const next = text.slice(0, start) + ins + text.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      const pos = start + ins.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const accept = (item) => {
    insertAt(value || "", sugg.start, sugg.end, item.insert);
    setSugg(null);
  };

  const pickFunction = (ins) => {
    const el = ref.current;
    const text = value || "";
    const s = el ? el.selectionStart : text.length;
    const e = el ? el.selectionEnd : text.length;
    const wrap = mode === "template" && !/\{\{[^}]*$/.test(text.slice(0, s)) ? `{{ ${ins} }}` : ins;
    insertAt(text, s, e, wrap);
    setHelp(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <textarea
          ref={ref}
          rows={rows}
          spellCheck={false}
          placeholder={placeholder}
          className="w-full border border-EAECF0 rounded px-2 py-1 pr-7 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-focus-color resize-y"
          value={value || ""}
          onChange={(e) => {
            onChange(e.target.value);
            refresh(e.target.value, e.target.selectionStart);
          }}
          onClick={(e) => refresh(e.target.value, e.target.selectionStart)}
          onBlur={() => setTimeout(() => setSugg(null), 150)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (!sugg) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setSugg({ ...sugg, index: (sugg.index + 1) % sugg.items.length });
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setSugg({ ...sugg, index: (sugg.index - 1 + sugg.items.length) % sugg.items.length });
            } else if (e.key === "Enter" || e.key === "Tab") {
              e.preventDefault();
              accept(sugg.items[sugg.index]);
            } else if (e.key === "Escape") {
              setSugg(null);
            }
          }}
        />
        <button type="button" title="Functions" className="absolute right-1 top-1 text-667085 hover:text-primary" onClick={() => setHelp(!help)}>
          <SquareFunction size={16} />
        </button>
      </div>
      {sugg && (
        <div className="absolute z-50 left-0 right-0 mt-0.5 bg-white border border-EAECF0 rounded shadow-lg max-h-48 overflow-auto">
          {sugg.items.map((s, i) => (
            <div
              key={s.insert + i}
              className={`px-2 py-1 text-[11px] font-mono cursor-pointer flex justify-between ${i === sugg.index ? "bg-D5E6Ff" : "hover:bg-f3f8ff"}`}
              onMouseDown={(e) => {
                e.preventDefault();
                accept(s);
              }}
            >
              <span>{s.label}</span>
              <span className="text-667085 text-[10px]">{s.kind}</span>
            </div>
          ))}
        </div>
      )}
      {help && (
        <div className="absolute z-50 left-0 right-0 mt-0.5 bg-white border border-EAECF0 rounded shadow-lg">
          <FunctionHelp onPick={pickFunction} />
        </div>
      )}
      {result && (
        <div className={`mt-1 text-[10px] font-mono break-all ${result.error ? "text-F51D00" : "text-667085"}`}>
          {result.error ? `⚠ ${result.error}` : `= ${result.value === "" ? "(blank)" : result.value}`}
        </div>
      )}
    </div>
  );
}
